import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Form, Button } from 'react-bootstrap';
import AdminDataTable from './AdminDataTable';
import Swal from 'sweetalert2';
import { FaGift, FaPlus, FaTrash, FaEdit, FaSearch } from 'react-icons/fa';
import Navigation from './Navigation';
import useAuth from '../../hooks/useAuth';
import AdminPageHeader from './AdminPageHeader';

function Rewards() {
  const [rewards, setRewards] = useState([]);
  const [filteredRewards, setFilteredRewards] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { verifyAndRefreshToken } = useAuth();

  const fetchRewards = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('sessionToken');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}admin/allrewards`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const json = await res.json();
      if (json.error === 0) {
        setRewards(json.data);
        setFilteredRewards(json.data);
      } else {
        Swal.fire('Error', 'Failed to fetch rewards', 'error');
      }
    } catch (err) {
      Swal.fire('Error', 'Failed to fetch rewards', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem('sessionToken');
      if (!token) return navigate('/loginadmin');

      const isValid = await verifyAndRefreshToken(token);
      if (isValid) fetchRewards();
    };

    init();
  }, [navigate, verifyAndRefreshToken, fetchRewards]);

  const handleSearch = useCallback((e) => {
    const value = e.target.value;
    setSearch(value);
    setFilteredRewards(
      rewards.filter(r => r.name.toLowerCase().includes(value.toLowerCase()))
    );
  }, [rewards]);

  const columns = [
    { name: 'ID', selector: row => row.id, sortable: true },
    { name: 'Name', selector: row => row.name, sortable: true },
    { name: 'Description', selector: row => row.description },
    { name: 'Points Required', selector: row => row.points_required },
    { name: 'Stock', selector: row => row.stock },
    { name: 'Outlet ID', selector: row => row.outlet_id },
    {
      name: 'Actions',
      cell: row => (
        <div className="d-flex gap-2">
          <Button
            variant="warning"
            size="sm"
            onClick={() => navigate(`/editreward/${row.id}`)}
          >
            <FaEdit />
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => handleDelete(row.id)}
          >
            <FaTrash />
          </Button>
        </div>
      )
    },
  ];

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: 'Delete Reward?',
      text: 'This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      confirmButtonColor: '#d33',
    });

    if (confirm.isConfirmed) {
      const token = localStorage.getItem('sessionToken');
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}admin/deletereward`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ id }),
        });

        const json = await res.json();
        if (json.error === 0) {
          Swal.fire('Deleted!', 'Reward has been deleted.', 'success');
          fetchRewards();
        } else {
          Swal.fire('Error', json.message || 'Failed to delete reward', 'error');
        }
      } catch (err) {
        Swal.fire('Error', 'Failed to delete reward', 'error');
      }
    }
  };

  return (
    <div>
      <Navigation />
      <Container fluid className="admin-page-shell mt-4 px-4">
        <AdminPageHeader
          icon={FaGift}
          eyebrow="Reward catalog"
          title="Reward Administration"
          description="Kelola katalog reward, persyaratan poin, dan ketersediaan hadiah secara konsisten untuk seluruh program loyalitas."
          breadcrumbs={[
            { label: 'Dashboard', to: '/dashboardadmin' },
            { label: 'Reward Administration' },
          ]}
          actions={(
            <Button variant="primary" onClick={() => navigate('/addreward')}>
              <FaPlus /> Create Reward
            </Button>
          )}
          meta={(
            <span className="soft-stat">
              {filteredRewards.length} rewards
            </span>
          )}
        />

        <div className="section-card table-card">
          <div className="table-toolbar">
            <span className="status-chip">{filteredRewards.length} rewards</span>
            <div className="control-with-icon search-control">
              <span className="control-with-icon__icon">
                <FaSearch />
              </span>
              <Form.Control
                type="text"
                placeholder="Search by reward name"
                value={search}
                onChange={handleSearch}
              />
            </div>
          </div>

          <div className="card-body pt-0">
            <AdminDataTable
              columns={columns}
              data={filteredRewards || []}
              pagination
              progressPending={loading}
              highlightOnHover
              pointerOnHover
              striped
              responsive
              emptyTitle="No rewards found"
              emptyDescription="Tambahkan reward baru untuk melengkapi katalog penukaran poin."
            />
          </div>
        </div>
      </Container>
    </div>
  );
}

export default Rewards;
